import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useDeepCompareEffect } from 'ahooks';
import FileItem, { IFileItemProps } from '../FileItem';
import { getElementStyleMathValue } from '@gp/utils/common';

import type { IBaseProps } from '@home/types/common';
import type { IFile } from '../../types';

import './index.scss';

export interface IFileListProps extends IBaseProps {
  files?: IFile[] | null;
  renderActions?: IFileItemProps['renderActions'];
  onFileNameClick?: IFileItemProps['onFileNameClick'];
  onFileNameMouseEnter?: IFileItemProps['onFileNameMouseEnter'];
  onFileNameMouseLeave?: IFileItemProps['onFileNameMouseLeave'];
  onFileItemMouseEnter?: IFileItemProps['onMouseEnter'];
  onFileItemMouseLeave?: IFileItemProps['onMouseLeave'];
}

// 单个文件UI的宽度
const FILE_ITEM_WIDTH = 230;
// 单个文件UI的水平间距
const FILE_ITEM_HORIZONTAL_SPACING = 40;
// 单个文件UI的纵向间距
const FILE_ITEM_VERTICAL_SPACING = 16;

const calcFilesLengthPerRow = (container?: HTMLDivElement | null): number => {
  if (!container) return 0;
  const containerWidth = getElementStyleMathValue(container, 'width');
  return Math.floor((containerWidth + FILE_ITEM_HORIZONTAL_SPACING) / (FILE_ITEM_WIDTH + FILE_ITEM_HORIZONTAL_SPACING));
};

const useLengthPerRow = (container?: React.RefObject<HTMLDivElement | null>, files?: IFile[] | null) => {
  const [length, setLength] = useState(0);

  const updateLength = () => {
    if (!container?.current) return;
    setLength(calcFilesLengthPerRow(container.current));
  };

  useDeepCompareEffect(() => {
    updateLength();
  }, [files]);

  useEffect(() => {
    window.addEventListener('resize', updateLength, false);

    return () => {
      window.removeEventListener('resize', updateLength, false);
    };
  }, []);

  return length;
};

const FileList: React.FC<IFileListProps> = (props) => {
  const {
    style,
    className,
    files,
    renderActions,
    onFileNameClick,
    onFileNameMouseEnter,
    onFileNameMouseLeave,
    onFileItemMouseEnter,
    onFileItemMouseLeave,
  } = props;

  const container = useRef<HTMLDivElement>(null);

  const lengthPerRow = useLengthPerRow(container, files);

  const filesLength = files?.length || 0;
  const rows = Math.ceil(filesLength / lengthPerRow);
  // 最后一行的开始索引
  const specialIndex = (rows - 1) * lengthPerRow;

  return (
    <div ref={container} style={style} className={classnames('dbg-common-file-list', className)}>
      <ul className="dbg-common-file-list__list">
        {files?.map((file, fileIdx) => {
          if (!file) return null;
          return (
            <li
              key={fileIdx}
              style={{
                /**
                 * 1、最后一个marginRight为0
                 * 2、每一行最后一个marginRight为0
                 * 3、其余情况marginRight为FILE_ITEM_HORIZONTAL_SPACING
                 */
                marginRight:
                  fileIdx === filesLength - 1 || (fileIdx + 1) % lengthPerRow === 0 ? 0 : FILE_ITEM_HORIZONTAL_SPACING,
                /**
                 * 1、最后一行marginBottom为0
                 * 3、其余marginBottom为FILE_ITEM_VERTICAL_SPACING
                 */
                marginBottom: fileIdx >= specialIndex ? 0 : FILE_ITEM_VERTICAL_SPACING,
                flexBasis: FILE_ITEM_WIDTH,
              }}
              className="dbg-common-file-list__item"
            >
              <FileItem
                file={file}
                renderActions={renderActions}
                onFileNameClick={onFileNameClick}
                onFileNameMouseEnter={onFileNameMouseEnter}
                onFileNameMouseLeave={onFileNameMouseLeave}
                onMouseEnter={onFileItemMouseEnter}
                onMouseLeave={onFileItemMouseLeave}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default FileList;
